<script setup lang="ts">
// Reka-backed tooltip. Wrap the trigger element; pass its text via `label`.
// Replaces the apps' hand-rolled `v-tooltip` directive + single floating layer
// with Reka's Floating-UI positioning (collision-aware flip/clamp), focus +
// hover coordination, and `aria-describedby` wiring - all for free.
//
//   <Tooltip label="Delete"><button>…</button></Tooltip>
//   <Tooltip :label="cond ? 'Out of credits' : undefined" side="right">…</Tooltip>
//
// An empty / undefined `label` renders the slotted child bare (no tooltip), so
// callers can bind a maybe-empty expression exactly like the old directive's
// no-op-on-falsy contract. `as-child` merges the trigger wiring onto the single
// element in the slot - that element must forward attrs (native elements do;
// custom components need `inheritAttrs`).
//
// Shared delay + the "skip the delay when moving between adjacent tooltips" UX
// come from a single <TooltipProvider> mounted once at each app root.
import {
  TooltipRoot,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
  TooltipArrow,
} from 'reka-ui';

withDefaults(defineProps<{
  /** The tooltip text. Falsy => no tooltip (child rendered bare). */
  label?: string | null | undefined;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  /** Suppress the tooltip while keeping the child (e.g. an open menu owns focus). */
  disabled?: boolean;
}>(), {
  side: 'top',
  sideOffset: 8,
  disabled: false,
});
</script>

<template>
  <slot v-if="!label || disabled" />
  <TooltipRoot v-else>
    <TooltipTrigger as-child>
      <slot />
    </TooltipTrigger>
    <TooltipPortal>
      <TooltipContent
        class="hq-tip"
        :side="side"
        :side-offset="sideOffset"
        :collision-padding="6"
        role="tooltip"
      >
        {{ label }}
        <TooltipArrow class="hq-tip__arrow" :width="10" :height="5" />
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
</template>

<!-- Unscoped: TooltipContent is portalled to <body> by Reka, so a scoped style
     hash would never reach it (same reason Dialog/Menu styles are global). All
     classes are `hq-tip`-prefixed. Tokens resolve off :root in both apps. -->
<style>
.hq-tip {
  z-index: 9500;                /* above menus (9000), dialogs, toasts, lightbox */
  max-width: 16rem;
  padding: 5px 9px;
  background: var(--surface-panel);
  color: var(--ink-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-chip);
  box-shadow: var(--shadow-overlay);
  font-size: var(--text-xs);
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 0;
  white-space: normal;
  text-align: center;
  word-break: break-word;
  user-select: none;
  /* Reka writes the origin for the chosen side; animate the reveal off it. */
  transform-origin: var(--reka-tooltip-content-transform-origin);
}
.hq-tip[data-state="delayed-open"],
.hq-tip[data-state="instant-open"] {
  animation: hq-tip-in var(--dur-1, 0.12s) var(--ease-out, ease);
}
.hq-tip__arrow { fill: var(--surface-panel); }

@keyframes hq-tip-in {
  from { opacity: 0; transform: scale(0.96); }
}
@media (prefers-reduced-motion: reduce) {
  .hq-tip[data-state] { animation: none; }
}
</style>
